export const ROLE_WORKFLOW_VIEWER = "workflow:viewer";
export const ROLE_WORKFLOW_ADMIN = "workflow:admin";
export const ROLE_USER_ADMIN = "user:admin";
export const ROLE_ADMIN = "admin";
export const ROLE_COLLECTION_VIEW = "collection:view";
export const ROLE_COLLECTION_COMPLETE = "collection:complete";
export const ROLE_COLLECTION_MANAGE = "collection:manage";

export const ROLES = {
	WORKFLOW_VIEWER: ROLE_WORKFLOW_VIEWER,
	WORKFLOW_ADMIN: ROLE_WORKFLOW_ADMIN,
	USER_ADMIN: ROLE_USER_ADMIN,
	COLLECTION_VIEW: ROLE_COLLECTION_VIEW,
	COLLECTION_COMPLETE: ROLE_COLLECTION_COMPLETE,
	COLLECTION_MANAGE: ROLE_COLLECTION_MANAGE,
	ADMIN: ROLE_ADMIN,
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

export const ROLE_METADATA: Record<
	Role,
	{ label: string; description: string; color: "info" | "warning" | "error" }
> = {
	[ROLE_WORKFLOW_VIEWER]: {
		label: "Workflow Viewer",
		description: "Can view workflow states and data",
		color: "info",
	},
	[ROLE_WORKFLOW_ADMIN]: {
		label: "Workflow Admin",
		description: "Can manage workflows (start, cancel, retry)",
		color: "warning",
	},
	[ROLE_USER_ADMIN]: {
		label: "User Admin",
		description: "Can manage users and their roles",
		color: "error",
	},
	[ROLE_COLLECTION_VIEW]: {
		label: "Collection Viewer",
		description: "Can view collections",
		color: "info",
	},
	[ROLE_COLLECTION_COMPLETE]: {
		label: "Collection Completer",
		description: "Can mark collections as complete",
		color: "warning",
	},
	[ROLE_COLLECTION_MANAGE]: {
		label: "Collection Manager",
		description: "Can manage collections (create, update, delete)",
		color: "error",
	},
	[ROLE_ADMIN]: {
		label: "Administrator",
		description: "Full access to all admin functions",
		color: "error",
	},
};

export function hasRole(userRoles: string[], requiredRole: string): boolean {
	if (userRoles.includes(requiredRole)) {
		return true;
	}

	if (userRoles.includes(ROLE_ADMIN)) {
		return true;
	}

	const [domain] = requiredRole.split(":");
	if (domain && userRoles.includes(`${domain}:admin`)) {
		return true;
	}

	if (requiredRole === ROLE_WORKFLOW_VIEWER && userRoles.includes(ROLE_WORKFLOW_ADMIN)) {
		return true;
	}

	if (userRoles.includes(ROLE_COLLECTION_MANAGE)) {
		if (requiredRole === ROLE_COLLECTION_VIEW || requiredRole === ROLE_COLLECTION_COMPLETE) {
			return true;
		}
	}

	if (requiredRole === ROLE_COLLECTION_VIEW && userRoles.includes(ROLE_COLLECTION_COMPLETE)) {
		return true;
	}

	return false;
}

export function canViewWorkflows(userRoles: string[]): boolean {
	return hasRole(userRoles, ROLE_WORKFLOW_VIEWER) || hasRole(userRoles, ROLE_WORKFLOW_ADMIN);
}

export function canManageWorkflows(userRoles: string[]): boolean {
	return hasRole(userRoles, ROLE_WORKFLOW_ADMIN);
}

export function canManageUsers(userRoles: string[]): boolean {
	return hasRole(userRoles, ROLE_USER_ADMIN);
}

export function canViewCollections(userRoles: string[]): boolean {
	return hasRole(userRoles, ROLE_COLLECTION_VIEW);
}

export function canCompleteCollections(userRoles: string[]): boolean {
	return hasRole(userRoles, ROLE_COLLECTION_COMPLETE);
}

export function canManageCollections(userRoles: string[]): boolean {
	return hasRole(userRoles, ROLE_COLLECTION_MANAGE);
}
